import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {
  ANALYTICS_MODULE,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {ActionOrigin, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import useTimingEstimate, {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {
  getExperimentUnknownErrorAction,
  onOpenAfterExperimentCreate,
  onSuccessAfterExperimentCreate,
} from 'studies/sidepanel/ExperimentCreationTrayTask';
import {onCreateProcedureRuns} from 'studies/sidepanel/onCreateProcedureRuns';
import {
  BulkCreateExperimentsParam,
  CreateExperimentsResponse,
  ProcedureInformation,
  ProcedureRunDataEntryMode,
} from 'studies/StudyDAO';
import MultiBatchSelector from 'studies/typed/process-development/multi-batch-selector/MultiBatchSelector';
import {
  Condition,
  ExperimentDesignState,
  useExperimentDesignState,
} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {LabeledProcedureDropdown} from 'studies/typed/shared/LabeledProcedureDropdown';
import {MULTI_PROCEDURE_RUN_LISTING_TABLE_QUERY} from 'studies/typed/shared/MultiProcedureRunListingTable';
import ProcedureRunDescriptionEditor from 'studies/typed/shared/ProcedureRunDescriptionEditor';
import {ProcedureRunDesignEditor} from 'studies/typed/shared/ProcedureRunDesignEditor';
import {isNumConditionReplicatesValid} from 'studies/typed/shared/procedureRunStudyValidationUtils';
import QueryErrorHandlingUnitOpLinkingEditor from 'studies/typed/shared/unit-op-linking-flowchart/QueryErrorHandlingUnitOpLinkingEditor';
import useUnitOpLinkingState from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import {UnitOpLinkingStateStatus} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {
  getProcedureRunConditionTextForProcedureType,
  getProcedureRunUIStringForStudyType,
  getProcedureTypeForStudyType,
} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton, SharedModalProps} from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';

import {ADD_PROCEDURE_RUN_BUTTON_QUERY} from '../typed/utils/ui/AddProcedureRunButton';
import {LINK_TO_EXISTING_UNIT_OPERATIONS_BUTTON_QUERY} from '../typed/utils/ui/LinkToExistingUnitOperationsButton';
import {
  createSourcePMEIsToTemplatePMDVsMapping,
  getDestinationPMEIIds,
  getDestinationPMEIs,
} from './AddProcedureRunModalUtils';
import ConfirmCancelModal from './ConfirmCancelModal';
import {NoMethodWarningBanner, NoProcedureWarningBanner} from './ModalWarningBanners';

export interface AddProcedureRunModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: () => void;
  studyType: StudyType;
  studyId: string;
  initialProcedureId?: string;
  hasExistingUnitOpsToLink: boolean;
}

enum AddProcedureRunStepKey {
  Metadata = 'Metadata',
  Experiment = 'Experiment',
  UnitOpInstanceLinking = 'UnitOpInstanceLinking',
}

/*
 * True if provided metadata is complete, valid, and should allow proceeding to design the experiment,
 * false otherwise.
 */
function validateMetadata(state: ExperimentDesignState): boolean {
  return state.procedureId != null;
}

function getButtonsByStep(
  currentStep: AddProcedureRunStepKey,
  updateCurrentStep: (step: AddProcedureRunStepKey) => void,
  submissionDisabled: boolean,
  isMetadataValid: boolean,
  isUnitOpInstanceLinkingEnabled: boolean,
  studyType: StudyType,
  hasExistingUnitOpsToLink: boolean
): Array<ModalButton> {
  switch (currentStep) {
    case AddProcedureRunStepKey.Metadata:
      return [
        {cancel: true, disabled: false, text: 'Cancel'},
        {
          disabled: !isMetadataValid,
          text: 'Next',
          style: ButtonStyle.SECONDARY,
          onClick: () => {
            updateCurrentStep(AddProcedureRunStepKey.Experiment);
          },
          dataTestAttrs: {component: 'AddProcedureRunModal', element: 'nextButton'},
        },
      ];
    case AddProcedureRunStepKey.Experiment:
      if (isUnitOpInstanceLinkingEnabled && hasExistingUnitOpsToLink) {
        return [
          {
            disabled: false,
            submit: false,
            text: 'Back',
            style: ButtonStyle.TERTIARY_GRAY,
            onClick: () => {
              updateCurrentStep(AddProcedureRunStepKey.Metadata);
            },
          },
          {
            disabled: submissionDisabled,
            submit: true,
            text: `Add ${getProcedureRunUIStringForStudyType(studyType)}`,
            style: ButtonStyle.TERTIARY_BLUE,
            dataTestAttrs: {component: 'AddProcedureRunModal', element: 'submitButton'},
          },
          {
            disabled: submissionDisabled,
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: () => {
              updateCurrentStep(AddProcedureRunStepKey.UnitOpInstanceLinking);
            },
            dataTestAttrs: {component: 'AddProcedureRunModal', element: 'nextButton'},
          },
        ];
      } else {
        return [
          {cancel: true, disabled: false, text: 'Cancel'},
          {
            disabled: false,
            submit: false,
            text: 'Back',
            style: ButtonStyle.TERTIARY_GRAY,
            onClick: () => {
              updateCurrentStep(AddProcedureRunStepKey.Metadata);
            },
          },
          {
            disabled: submissionDisabled,
            submit: true,
            text: `Add ${getProcedureRunUIStringForStudyType(studyType)}`,
            style: ButtonStyle.SECONDARY,
            dataTestAttrs: {component: 'AddProcedureRunModal', element: 'submitButton'},
          },
        ];
      }
    case AddProcedureRunStepKey.UnitOpInstanceLinking:
      return [
        {
          disabled: false,
          submit: false,
          text: 'Back',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: () => {
            updateCurrentStep(AddProcedureRunStepKey.Experiment);
          },
        },
        {
          disabled: submissionDisabled,
          submit: true,
          text: `Add ${getProcedureRunUIStringForStudyType(studyType)}`,
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'AddProcedureRunModal', element: 'submitButton'},
        },
      ];
    default:
      assert(false, `Unexpected key: ${assertNever(currentStep)}`);
  }
}

function modalOptions(stepKey: AddProcedureRunStepKey, studyType: StudyType): SharedModalProps {
  if (stepKey === AddProcedureRunStepKey.Metadata) {
    return {mediumLarge: true};
  }
  if (stepKey === AddProcedureRunStepKey.Experiment) {
    return {
      extraLarge: true,
      bodyPadded: false,
      fullHeight: true,
      footer: (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Upon creation, tasks will be generated and you won't be able to edit ${getProcedureRunConditionTextForProcedureType(
            getProcedureTypeForStudyType(studyType),
            {plural: true}
          )}.`}
        />
      ),
    };
  }
  if (stepKey === AddProcedureRunStepKey.UnitOpInstanceLinking) {
    return {extraLarge: true, bodyPadded: false, fullHeight: true};
  }
  return {};
}

function getSteps(
  isUnitOpInstanceLinkingEnabled: boolean,
  hasExistingUnitOpsToLink: boolean
): Array<{key: AddProcedureRunStepKey; title: string}> {
  const metadataStep = {key: AddProcedureRunStepKey.Metadata, title: 'Set up'};
  const experimentStep = {key: AddProcedureRunStepKey.Experiment, title: `Enter design`};
  const unitOpInstanceLinkingStep = {
    key: AddProcedureRunStepKey.UnitOpInstanceLinking,
    title: 'Link to existing unit operations',
  };
  const steps = [metadataStep, experimentStep];
  if (isUnitOpInstanceLinkingEnabled && hasExistingUnitOpsToLink) {
    steps.push(unitOpInstanceLinkingStep);
  }
  return steps;
}

function getUnitOpLinkingExperimentStateUpdateFunctions(
  experimentDesignState: ExperimentDesignState,
  unitOpLinkingState: ReturnType<typeof useUnitOpLinkingState>,
  isUnitOpInstanceLinkingEnabled: boolean
): {
  setProcedureId: (procedureId: string) => void;
  setConditions: (conditions: Array<Condition> | null, prevIndexes: Array<number | null>) => void;
} {
  if (isUnitOpInstanceLinkingEnabled && unitOpLinkingState.status === UnitOpLinkingStateStatus.SUCCESS) {
    return {
      setProcedureId: (procedureId: string) => {
        experimentDesignState.setProcedureId(procedureId);
        unitOpLinkingState.setNewLinks([]);
      },
      setConditions: (conditions: Array<Condition> | null, prevIndexes: Array<number | null>) => {
        experimentDesignState.setConditions(conditions, prevIndexes);
        if (unitOpLinkingState.newLinks == null) {
          return;
        }
        const newDestinationPMEIIds = getDestinationPMEIIds(experimentDesignState.procedure, conditions);
        const newLinks = unitOpLinkingState.newLinks.filter((link) =>
          newDestinationPMEIIds.has(link.destinationPMEIId)
        );
        unitOpLinkingState.setNewLinks(newLinks);
      },
    };
  }
  return {
    setProcedureId: experimentDesignState.setProcedureId,
    setConditions: experimentDesignState.setConditions,
  };
}

function renderStep(
  stepKey: AddProcedureRunStepKey,
  props: AddProcedureRunModalProps,
  state: ExperimentDesignState,
  estimate: TimingEstimateResult,
  unitOpLinkingState: ReturnType<typeof useUnitOpLinkingState>,
  isMultiBatchEnabled: boolean,
  isUnitOpInstanceLinkingEnabled: boolean
): JSX.Element {
  /*
   * Note: the experiment design step for this function assumes a procedure-based experiment design. If we
   * have a study type where adding an experiment entails something different from designing a procedure-based
   * experiment, we may want to hoist the rendering function for the experiment design step into the typed
   * study adapter to allow for this differentiation.
   */
  const procedureType = getProcedureTypeForStudyType(props.studyType);
  const {setProcedureId, setConditions} = getUnitOpLinkingExperimentStateUpdateFunctions(
    state,
    unitOpLinkingState,
    isUnitOpInstanceLinkingEnabled
  );

  switch (stepKey) {
    case AddProcedureRunStepKey.Metadata:
      return (
        <div className={css(styles.metadataContainer)}>
          <LabeledProcedureDropdown
            procedureId={state.procedureId}
            setProcedureId={setProcedureId}
            onRequestCloseModal={props.onRequestClose}
            procedureType={procedureType}
            requiredField={true}
            dropdownDisabled={false}
            // We need the latest possible procedure version id because that gets stamped into the new run
            procedureFetchPolicy="network-only"
          />
          <ProcedureRunDescriptionEditor
            setDescription={state.setDescription}
            description={state.description}
            isBeingCreated={true}
          />
          {isMultiBatchEnabled && (
            <MultiBatchSelector
              dataEntryMode={state.dataEntryMode}
              onSelectDataEntryMode={state.setDataEntryMode}
              isDisabled={false}
            />
          )}
        </div>
      );
    case AddProcedureRunStepKey.Experiment:
      if (state.procedureId == null) {
        return <NoProcedureWarningBanner procedureType={procedureType} />;
      }
      if (state.procedure == null || state.context == null || state.conditions == null) {
        return <HorizontalThrobber />;
      }
      if (state.selectedMethodId == null) {
        return <NoMethodWarningBanner procedureType={procedureType} />;
      }

      return (
        <ProcedureRunDesignEditor
          conditions={state.conditions}
          setConditions={setConditions}
          procedureRunInputs={state.experimentalInputs}
          setProcedureRunInputs={state.setExperimentalInputs}
          context={state.context}
          procedureFlowchart={state.procedure.procedureFlowchart}
          procedureMethods={state.procedure.methods}
          selectedMethodId={state.selectedMethodId}
          setSelectedMethodId={state.setSelectedMethodId}
          methodExecutionLimitState={state.methodExecutionLimitState}
          estimate={estimate}
          procedureType={procedureType}
        />
      );

    case AddProcedureRunStepKey.UnitOpInstanceLinking:
      assert(state.procedure != null, 'Procedure should not be null');
      assert(state.conditions != null, 'Conditions should not be null');
      return (
        <div className={css(styles.modalBodyContainer)}>
          <div className={css(styles.flowchartWrapper)}>
            <QueryErrorHandlingUnitOpLinkingEditor unitOpLinkingState={unitOpLinkingState} />
          </div>
        </div>
      );

    default:
      assert(false, `Unexpected key: ${assertNever(stepKey)}`);
  }
}

export default function AddProcedureRunModal(props: AddProcedureRunModalProps): JSX.Element {
  const isMultiBatchEnabled = getTypedStudyAdapter(props.studyType).isMultiBatchEnabled;
  const isUnitOpInstanceLinkingEnabled = getTypedStudyAdapter(
    props.studyType
  ).isUnitOperationInstanceLinkingEnabled;
  const state = useExperimentDesignState(
    {initialFolderId: null, initialProcedureId: props.initialProcedureId ?? null},
    isMultiBatchEnabled
      ? ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER
      : ProcedureRunDataEntryMode.INDIVIDUAL_CONDITION_REPLICATES
  );
  const orderedDestinationPMEIs = useMemo(
    () => getDestinationPMEIs(state.procedure, state.conditions),
    [state.procedure, state.conditions]
  );
  const unitOpLinkingState = useUnitOpLinkingState(props.studyId, {pmeis: orderedDestinationPMEIs});
  const sourcePMEIIdsByTemplatePMDVId = useMemo(() => {
    if (unitOpLinkingState.status === UnitOpLinkingStateStatus.SUCCESS) {
      return createSourcePMEIsToTemplatePMDVsMapping(
        unitOpLinkingState.newLinks,
        orderedDestinationPMEIs,
        state.procedure,
        state.conditions
      );
    } else {
      return [];
    }
  }, [unitOpLinkingState, orderedDestinationPMEIs, state.procedure, state.conditions]);
  const [isBackground, setIsBackground] = useState(false);
  const steps = getSteps(isUnitOpInstanceLinkingEnabled, props.hasExistingUnitOpsToLink);
  const [currentStepKey, setCurrentStepKey] = useState(steps[0]?.key);
  const isValidExperiment = isNumConditionReplicatesValid(state.methodExecutionLimitState);
  const estimateResult = useTimingEstimate<BulkCreateExperimentsParam>(
    '/1/api/studies/experiments:estimate',
    state.experimentParam != null && isValidExperiment ? {creates: [state.experimentParam]} : null
  );
  const stepAllowsSubmission =
    currentStepKey === AddProcedureRunStepKey.Experiment ||
    currentStepKey === AddProcedureRunStepKey.UnitOpInstanceLinking;

  const analyticsMetadata: AnalyticsMetadataForContext = {
    actionOrigin: ActionOrigin.STUDY_OVERVIEW_PAGE,
    modalType: 'add_experiment',
  };
  const analyticsModule = ANALYTICS_MODULE.STUDIES;

  const experimentParamsForDAO = useMemo((): ProcedureInformation | null => {
    if (!state.experimentParam) {
      return null;
    }
    const baseParam: ProcedureInformation = state.experimentParam;

    if (state.conditions != null && sourcePMEIIdsByTemplatePMDVId.length > 0) {
      const conditions = state.conditions;
      const newExperimentConditions = (baseParam.experimentConditions ?? []).map(
        (condition, conditionIndex) => {
          assert(
            sourcePMEIIdsByTemplatePMDVId.length === conditions.length,
            'Source PMEIs array must have same length as number of conditions'
          );
          assert(
            sourcePMEIIdsByTemplatePMDVId[conditionIndex].length === conditions[conditionIndex].numReplicates,
            'Source PMEIs array by condition must have same length as number of replicates'
          );
          // newReplicateInfos contains the source PMEIs for each condition replicate
          const newReplicateInfos = (condition.replicateInfos ?? []).map((replicateInfo, replicateIndex) => {
            const sourceData = sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex];
            return {
              ...replicateInfo,
              sourcePMEIIdsByTemplatePMDVId: sourceData !== undefined ? sourceData : {},
            };
          });
          return {...condition, replicateInfos: newReplicateInfos};
        }
      );
      return {...baseParam, experimentConditions: newExperimentConditions};
    } else {
      return baseParam;
    }
  }, [state, sourcePMEIIdsByTemplatePMDVId]);

  const handleSubmit =
    experimentParamsForDAO != null && stepAllowsSubmission && isValidExperiment
      ? async (): Promise<void> => {
          await onCreateProcedureRuns(props.studyId, props.studyType, [experimentParamsForDAO], {
            onSuccess: async (response: CreateExperimentsResponse) => {
              await onSuccessAfterExperimentCreate(props.studyId, response, props.studyType);
              Analytics.trackEventWithModule(
                COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
                analyticsModule,
                analyticsMetadata
              );
              getApolloClient().refetchQueries({
                include: [
                  MULTI_PROCEDURE_RUN_LISTING_TABLE_QUERY,
                  LINK_TO_EXISTING_UNIT_OPERATIONS_BUTTON_QUERY,
                  ADD_PROCEDURE_RUN_BUTTON_QUERY,
                ],
              });
              props.onRequestClose();
            },
            onOpen: (response: CreateExperimentsResponse) => {
              // we currently only support creating one procedure run per request
              onOpenAfterExperimentCreate(props.studyId, response.creates[0].id);
            },
            onBackground: (isBackground: boolean) => {
              setIsBackground(isBackground);
            },
            onUnknownError: getExperimentUnknownErrorAction(props.studyId, props.studyType),
          });
        }
      : null;

  if (isBackground) {
    return <></>;
  }

  const handleClose = async (): Promise<void> => {
    const confirmCancel = await runModal(ConfirmCancelModal, {
      userFacingContextName: `${getProcedureRunUIStringForStudyType(props.studyType)} creation`,
    });
    if (confirmCancel != null) {
      Analytics.trackEventWithModule(
        COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
        analyticsModule,
        analyticsMetadata
      );
      props.onRequestClose();
    }
  };

  const handleUpdateCurrentStep = (nextStepKey: AddProcedureRunStepKey): void => {
    const isMetadataValid = validateMetadata(state);
    switch (nextStepKey) {
      case AddProcedureRunStepKey.Metadata: {
        setCurrentStepKey(nextStepKey);
        break;
      }
      // Metadata needs to be valid to enter these steps
      case AddProcedureRunStepKey.Experiment: {
        if (isMetadataValid) {
          setCurrentStepKey(nextStepKey);
        }
        break;
      }
      case AddProcedureRunStepKey.UnitOpInstanceLinking: {
        if (handleSubmit != null && isMetadataValid) {
          setCurrentStepKey(nextStepKey);
        }
        break;
      }
      default:
        assert(false, `Unexpected key: ${assertNever(nextStepKey)}`);
    }
  };

  return (
    <Modal
      title={
        <div className={css(styles.titleWrapper)}>
          <span>Add {getProcedureRunUIStringForStudyType(props.studyType)}</span>
          <StepNav<AddProcedureRunStepKey>
            steps={steps}
            statusByStep={{Metadata: null, Experiment: null, UnitOpInstanceLinking: null}}
            currentStepKey={currentStepKey}
            onUpdateCurrentStep={handleUpdateCurrentStep}
            variant={StepNavVariant.FLAT}
          />
        </div>
      }
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      buttons={getButtonsByStep(
        currentStepKey,
        setCurrentStepKey,
        !handleSubmit,
        validateMetadata(state),
        isUnitOpInstanceLinkingEnabled,
        props.studyType,
        props.hasExistingUnitOpsToLink
      )}
      {...modalOptions(currentStepKey, props.studyType)}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
    >
      {renderStep(
        currentStepKey,
        props,
        state,
        estimateResult,
        unitOpLinkingState,
        isMultiBatchEnabled,
        isUnitOpInstanceLinkingEnabled
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  flowchartWrapper: {flexGrow: 1},
  titleWrapper: {display: 'flex', flexWrap: 'wrap', columnGap: '50px', rowGap: 10},
  metadataContainer: {padding: 8},
  loadingDiv: {paddingBottom: 5},
  modalBodyContainer: {display: 'flex', flexDirection: 'column', height: '100%'},
});
